import { pathFactory } from '@hwm/ui-lib';
import { lazy } from 'react';
import { AppFeatureFlags, AppPermissions } from '@root/appPermissions';
import {
  AudioAnalysisDetailsPathsOptions,
  AudioListPathsOptions,
  CorrelationPathsOptions,
  FunctionsPathsOptions,
  accountPathsFactory,
  correlationAppPath,
  createParametrizedPath,
  siteAndAccountPathsFactory,
  siteBulkActionsAppPath,
} from './routesPaths.utils';

const SitesMapComponent = lazy(() => import(`@features/SitesMap`));
const StatsComponent = lazy(() => import(`@features/Statistics`));
const SettingsComponent = lazy(() => import(`@features/Settings`));
const AuditComponent = lazy(() => import('@features/Audit'));
const LnSComponent = lazy(() => import(`@features/LnS`));
const CorrelationListComponent = lazy(() => import(`@features/Correlations`));
const LeakListComponent = lazy(() => import(`@features/LeakList`));
const NewLeaksComponent = lazy(() => import(`@features/NewLeaks`));
const LeakAnalysisComponent = lazy(() => import(`@features/LeakAnalysis`));
const SitesListComponent = lazy(() => import(`@features/SitesList`));
const AudioListComponent = lazy(() => import(`@features/AudioList`));
const Correlation = lazy(() => import(`@features/Correlation`));
const DeploymentsComponent = lazy(() => import('@features/Deployments'));
const MaintenanceComponent = lazy(() => import('@features/Maintenance'));
const FunctionsComponent = lazy(() => import('@features/Functions'));
const SurveysListComponent = lazy(() => import('@features/SurveysList'));
const AudioAnalysisSummaryComponent = lazy(() => import('@features/AudioAnalysis/AudioAnalysisSummary'));
const AudioAnalysisDetailsComponent = lazy(() => import('@features/AudioAnalysis/AudioAnalysisDetails'));

export const AppPaths = {
  // other paths
  functions: pathFactory(FunctionsPathsOptions, FunctionsComponent, true),
  [correlationAppPath]: pathFactory(CorrelationPathsOptions, Correlation, true),
  audioList: pathFactory(AudioListPathsOptions, AudioListComponent, true),
  audioAnalysisDetails: pathFactory(AudioAnalysisDetailsPathsOptions, AudioAnalysisDetailsComponent, true),

  // with account paths
  statistics: pathFactory(accountPathsFactory({ basePath: 'dashboard' }), StatsComponent, true),
  leaks: pathFactory(accountPathsFactory({ basePath: 'leaks' }), LeakListComponent, true),
  deployments: pathFactory(accountPathsFactory({ basePath: 'deployments' }), DeploymentsComponent, true),
  audit: pathFactory(accountPathsFactory({ basePath: 'settings/audit' }), AuditComponent, true),
  settings: pathFactory(accountPathsFactory({ basePath: 'settings' }), SettingsComponent, true),
  correlations: pathFactory(accountPathsFactory({ basePath: 'correlations' }), CorrelationListComponent, true),
  surveys: pathFactory(
    {
      ...accountPathsFactory({
        basePath: 'surveys',
        featureFlag: AppFeatureFlags.MicrostreamSurveys,
      }),
      withSurveyDetails: createParametrizedPath(
        'surveys',
        'surveyDetails/:surveyDetailsId',
        ({ surveyDetailsId }) => `/surveys/surveyDetails/${surveyDetailsId}`,
        null,
        AppFeatureFlags.MicrostreamSurveys
      ),
    },
    SurveysListComponent,
    true
  ),
  audioAnalysisSummary: pathFactory(
    accountPathsFactory({
      basePath: 'audioAnalysis/summary',
      featureFlag: AppFeatureFlags.MLAudioAnalysis,
    }),
    AudioAnalysisSummaryComponent,
    true
  ),

  // with sites paths
  sitesMap: pathFactory(
    {
      ...siteAndAccountPathsFactory({
        basePath: 'sitesMap',
      }),
      siteLocationEdit: createParametrizedPath(
        'sitesMap',
        ':id/locationEdit',
        ({ id }) => `/sitesMap/${id}/locationEdit`
      ),
      withSurveyMeasurement: createParametrizedPath(
        'sitesMap',
        'surveyMeasurement/:surveyMeasurementId',
        ({ surveyMeasurementId }) => `/sitesMap/surveyMeasurement/${surveyMeasurementId}`
      ),
      withFlowData: createParametrizedPath(
        'sitesMap',
        'flowData/:flowDataId',
        ({ flowDataId }) => `/sitesMap/flowData/${flowDataId}`
      ),
      withSurveyDetails: createParametrizedPath(
        'sitesMap',
        'surveyDetails/:surveyDetailsId',
        ({ surveyDetailsId }) => `/sitesMap/surveyDetails/${surveyDetailsId}`
      ),
    },
    SitesMapComponent,
    true
  ),
  sites: pathFactory(
    {
      ...siteAndAccountPathsFactory({ basePath: 'sites' }),
      withBulkActions: { path: `/sites/${siteBulkActionsAppPath}` },
    },
    SitesListComponent,
    true
  ),
  lns: pathFactory(siteAndAccountPathsFactory({ basePath: 'lns' }), LnSComponent, true),
  newLeaks: pathFactory(
    {
      ...siteAndAccountPathsFactory({ basePath: 'newLeaks', featureFlag: AppFeatureFlags.NewLeaks }),
      withBulkActions: {
        path: `/newLeaks/${siteBulkActionsAppPath}`,
        featureFlag: AppFeatureFlags.NewLeaks,
      },
    },
    NewLeaksComponent,
    true
  ),
  leakAnalysis: pathFactory(
    {
      ...siteAndAccountPathsFactory({
        basePath: 'leakAnalysis',
        featureFlag: AppFeatureFlags.LeakAnalysis,
      }),
      withBulkActions: {
        path: `/leakAnalysis/${siteBulkActionsAppPath}`,
      },
    },
    LeakAnalysisComponent,
    true
  ),
  maintenance: pathFactory(
    {
      ...siteAndAccountPathsFactory({
        basePath: 'maintenance',
        permissions: [AppPermissions.CanSeeMaintenanceTab],
      }),
      withBulkActions: {
        path: `/maintenance/${siteBulkActionsAppPath}`,
        permissions: [AppPermissions.CanSeeMaintenanceTab],
      },
    },
    MaintenanceComponent,
    true
  ),
};
